/**
 * One learner's answers, in one course — the drill-in a grade cannot show.
 *
 * WHY THIS EXISTS. `AdminGrades.tsx` can already tell an admin a subsection's score. It
 * cannot tell them whether the learner ever opened a poll, a survey or an open response,
 * because those carry no score and never appear in a gradebook column. And even where a
 * score exists, a score is not the same question as "what did they write" — a dispute over
 * a grade is usually a dispute over what the platform recorded, not over arithmetic.
 *
 * FOUR STATES, NEVER TWO, AND THE FOURTH IS THE POINT. MEASURED on this deployment: 17 of 19
 * survey rows hold `{"choices": null}` — a row exists because the learner OPENED the block, not
 * because they answered it. Collapsing "seen" into "answered" (or into "not seen") is one
 * mistake this component exists to make impossible.
 *
 * The other one shipped anyway, on the server, and it is worse. A block type the backend could
 * not read was forced into "seen" — *"They did not answer."* — on a truthiness test of a blob
 * whose shape it did not know. The one learner on this deployment who has ever uploaded a file
 * was reported that way: Staff Graded Assignment keeps `{}` in the courseware state table and
 * files the real submission somewhere else. A confident negative is worse than a blank, because
 * an admin acts on it. `undetermined` is now a state in its own right and renders in its own
 * colour, never as a paler "seen".
 *
 * Every row's state comes from the server's own four-way `state`, and every label comes from
 * the server's own `legend` strings — never invented wording, because "seen" and "answered"
 * reading the same to an admin skimming a table is the whole failure mode.
 *
 * NO FAN-OUT. The index (`getLearnerAnswers`) is ONE request, made once per learner+course
 * when this component mounts. The per-block endpoint (`getLearnerBlockAnswer`) is fetched
 * ONLY when a human expands that exact row — `enabled: open` below — and cached with an
 * INFINITE `staleTime`, because a learner's already-submitted answer does not change while
 * an admin has the page open, so there is nothing a refetch on re-expand could ever learn.
 * `AdminGrades.tsx`'s header records what looping a per-item endpoint costs: 31.69s against
 * 2.60s over the same 37 enrolments, once one screen started computing a full breakdown per
 * row instead of reading what a single list request already carried. This file is the same
 * trap in miniature — one course can hold hundreds of blocks — and it is avoided the same
 * way: batch what is cheap, gate what is not behind an explicit click.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Banner, Icon, Spinner } from '../shared/ui';
import {
  getLearnerAnswers,
  getLearnerBlockAnswer,
  type AnswerBlockSummary,
  type AnswerState,
  type AnswerSubsection,
  type BlockAnswer,
  type FileUploadAnswer,
  type LearnerBlockAnswer,
  type OpenAssessmentAnswer,
  type ProblemAnswer,
} from '../shared/lib/answers';
import './learner-answers.css';

// The legend's reading order. `undetermined` sits last because it is the escape hatch rather
// than a step on a scale — putting it between `seen` and `answered` would imply it is one.
const STATE_ORDER: AnswerState[] = ['not_seen', 'seen', 'answered', 'undetermined'];

export function LearnerAnswers({
  courseId,
  courseName,
  username,
}: {
  courseId: string;
  courseName: string;
  username: string;
}) {
  // ONE request for this learner+course, made once. Every row below reads from this;
  // nothing here re-asks it per subsection or per block.
  const index = useQuery({
    queryKey: ['learner-answers', courseId, username],
    queryFn: () => getLearnerAnswers(courseId, username),
  });

  // Which rows are open. A `Set`, not a single id — an admin comparing two answers side by
  // side is the normal way this gets used, not a mistake to prevent.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (blockKey: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(blockKey)) next.delete(blockKey);
      else next.add(blockKey);
      return next;
    });

  if (index.isLoading) {
    return <Spinner label={`Loading ${username}'s answers in ${courseName}…`} />;
  }

  if (index.isError) {
    return (
      <Banner tone="danger">
        <strong>Could not load {username}&rsquo;s answers.</strong> Nothing has been changed.
        Reload this panel to try again.
      </Banner>
    );
  }

  const data = index.data;
  if (!data) return null;

  return (
    <div className="la-root">
      <LegendKey legend={data.legend} />

      {data.subsections.length === 0 ? (
        <p className="la-empty">
          {username} has not reached anything answerable in {courseName} yet.
        </p>
      ) : (
        <ul className="la-subsections">
          {data.subsections.map((subsection) => (
            <SubsectionRows
              key={subsection.block_key}
              courseId={courseId}
              username={username}
              subsection={subsection}
              legend={data.legend}
              expanded={expanded}
              onToggle={toggle}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * The three states, in the platform's own words. Shown once, above the list, so a state
 * that only shows as a single word per row ("Seen") still has its full meaning one glance
 * away — and so nobody has to guess what this UI's own wording means, because none of it is.
 */
function LegendKey({ legend }: { legend: Record<AnswerState, string> }) {
  return (
    <ul className="la-key">
      {STATE_ORDER.map((state) => (
        <li key={state} className="la-key__item" data-state={state}>
          <span className="la-key__dot" aria-hidden="true" />
          {legend[state]}
        </li>
      ))}
    </ul>
  );
}

function SubsectionRows({
  courseId,
  username,
  subsection,
  legend,
  expanded,
  onToggle,
}: {
  courseId: string;
  username: string;
  subsection: AnswerSubsection;
  legend: Record<AnswerState, string>;
  expanded: Set<string>;
  onToggle: (blockKey: string) => void;
}) {
  return (
    <li className="la-subsection">
      <header className="la-subsection__head">
        <h4>{subsection.title}</h4>
        {subsection.graded && subsection.assignment_type && (
          <span className="la-subsection__type">{subsection.assignment_type}</span>
        )}
      </header>
      <ul className="la-blocks">
        {subsection.blocks.map((block) => (
          <AnswerRow
            key={block.block_key}
            courseId={courseId}
            username={username}
            block={block}
            legend={legend}
            open={expanded.has(block.block_key)}
            onToggle={() => onToggle(block.block_key)}
          />
        ))}
      </ul>
    </li>
  );
}

function AnswerRow({
  courseId,
  username,
  block,
  legend,
  open,
  onToggle,
}: {
  courseId: string;
  username: string;
  block: AnswerBlockSummary;
  legend: Record<AnswerState, string>;
  open: boolean;
  onToggle: () => void;
}) {
  // Gated behind `open`, and `staleTime: Infinity` so re-collapsing and re-expanding the
  // same row never asks the server again — see the file header for why that would be a
  // request that could not learn anything.
  const detail = useQuery({
    queryKey: ['learner-answer', courseId, username, block.block_key],
    queryFn: () => getLearnerBlockAnswer(courseId, username, block.block_key),
    enabled: open,
    staleTime: Infinity,
  });

  // A missing possible score is not a zero-weight block — it is a block this course does
  // not grade at all (a poll, a survey), and the row says nothing about points for one.
  const hasScore = block.score_possible !== null && block.score_possible > 0;

  return (
    <li className="la-block" data-state={block.state}>
      <button type="button" className="la-block__row" aria-expanded={open} onClick={onToggle}>
        <Icon className="la-block__chevron" name={open ? 'expand_less' : 'expand_more'} />
        {/*
          TITLE FIRST, and the state pill after it. The other order was tried and shipped a
          row an admin could not read: the pill carries a full sentence from the payload and
          was `flex: none`, so it filled the row and pushed "Question 1" out of sight
          entirely — a browser screenshot is what caught it.

          The WORDING is still the payload's, unchanged, because the three states are the
          backend's vocabulary and a second set here would be free to drift from it. Only
          the pill's width gives way: it truncates, carries the full sentence as its tooltip,
          and the legend above the list always spells all three out in full.
        */}
        <span className="la-block__title">{block.title}</span>
        <span className="la-block__state" data-state={block.state} title={legend[block.state]}>
          {legend[block.state]}
        </span>
        {hasScore && (
          <span className="la-block__score">
            {block.score_earned ?? 0}/{block.score_possible}
          </span>
        )}
      </button>

      {open && (
        <div className="la-block__detail">
          {detail.isLoading && <Spinner label="Loading this answer…" />}
          {detail.isError && (
            <Banner tone="danger">
              Could not load this answer. Collapse the row and expand it again to retry.
            </Banner>
          )}
          {detail.data && <AnswerDetail data={detail.data} legend={legend} />}
        </div>
      )}
    </li>
  );
}

/**
 * What one block's answer looks like, once fetched.
 *
 * THE ORDER OF THESE BRANCHES IS LOAD-BEARING and it was wrong before. `!data.answer` came
 * first, so a block with no decode printed only its state sentence and RETURNED — the
 * "View submission history" link below it was unreachable for the exact rows that needed it,
 * because a row with no decode is precisely a row with no `answer`. The escape hatch is
 * therefore checked first now, and `data.submission_history_url` is the server's own signal
 * that it exists: its presence is the flag, never its content.
 */
function AnswerDetail({
  data,
  legend,
}: {
  data: LearnerBlockAnswer;
  legend: Record<AnswerState, string>;
}) {
  // Either a submission whose shape the server will not guess at, or a block type it cannot
  // read at all. Both are `undetermined`-or-`answered`-without-a-decode, and both get the
  // platform's own record rather than a sentence that sounds like a verdict.
  if (data.submission_history_url) {
    return (
      <p className="la-block__note">
        {data.note ?? "This block's answer could not be decoded here."}{' '}
        <a href={data.submission_history_url} target="_blank" rel="noreferrer">
          View submission history
        </a>
      </p>
    );
  }

  if (!data.answer) {
    return (
      <p className="la-block__note">
        {data.note ?? legend[data.state]}
        {/* Said out loud rather than left to be inferred from a state pill: when the
            submissions tables could not be read, "nothing here" is a failure of ours and not
            a fact about the learner. */}
        {data.submissions_consulted === false && (
          <>
            {' '}
            <strong>
              thinlms could not read the submissions store on this request, so anything filed
              there is not reflected above.
            </strong>
          </>
        )}
      </p>
    );
  }

  return <DecodedAnswer answer={data.answer} />;
}

function DecodedAnswer({ answer }: { answer: BlockAnswer }) {
  switch (answer.kind) {
    case 'problem':
      return <ProblemAnswerView answer={answer} />;
    case 'poll':
      return (
        <p className="la-block__answer">
          <strong>Choice:</strong> {answer.choice}
        </p>
      );
    case 'survey':
      return (
        <dl className="la-survey">
          {Object.entries(answer.choices).map(([question, choice]) => (
            <div key={question}>
              <dt>{question}</dt>
              <dd>{choice || <span className="admin-muted">No answer</span>}</dd>
            </div>
          ))}
        </dl>
      );
    case 'file_upload':
      return <FileUploadAnswerView answer={answer} />;
    case 'openassessment':
      return <OpenAssessmentAnswerView answer={answer} />;
    default:
      return null;
  }
}

/** When a submission was filed, in the reader's own locale. `null` stays absent, never "—". */
function submittedLine(submittedAt: string | null, attempt: number | null) {
  const parts: string[] = [];
  if (submittedAt) {
    const when = new Date(submittedAt);
    parts.push(`Submitted ${Number.isNaN(when.valueOf()) ? submittedAt : when.toLocaleString()}`);
  }
  if (attempt !== null && attempt !== undefined) parts.push(`attempt ${attempt}`);
  return parts.length ? parts.join(' · ') : null;
}

/**
 * A file upload — THE RECORD OF ONE, NOT A DOWNLOAD.
 *
 * Deliberately not a link, and the wording is chosen so nobody reads it as one. There is no
 * download route in thinlms, and the bytes behind an upload made before 2026-08-29 do not
 * exist: `/openedx/media` was unmounted container-local disk until that morning, so every file
 * written there was destroyed on the next pod restart with the database row left behind. A
 * link that 404s would be a third way for this screen to look like it works and not.
 */
function FileUploadAnswerView({ answer }: { answer: FileUploadAnswer }) {
  const when = submittedLine(answer.submitted_at, answer.attempt_number);
  return (
    <div className="la-problem">
      <p className="la-block__answer">
        <strong>File submitted:</strong>{' '}
        {answer.filename ?? <span className="admin-muted">name not recorded</span>}
        {answer.mimetype && <span className="admin-muted"> ({answer.mimetype})</span>}
      </p>
      {when && <p className="la-block__meta">{when}</p>}
      {!answer.finalized && (
        <p className="la-block__note">
          This upload is not finalised — the learner started it and did not complete the
          submission.
        </p>
      )}
      <p className="la-block__note">
        thinlms records the submission, not the file itself; there is no download from this
        screen.
      </p>
    </div>
  );
}

/** An Open Response submission: what they wrote, and what they attached. */
function OpenAssessmentAnswerView({ answer }: { answer: OpenAssessmentAnswer }) {
  const when = submittedLine(answer.submitted_at, answer.attempt_number);
  return (
    <div className="la-problem">
      {when && <p className="la-block__meta">{when}</p>}
      {answer.parts.length === 0 ? (
        <p className="la-block__note">This submission carries no written response.</p>
      ) : (
        answer.parts.map((text, index) => (
          <p key={index} className="la-block__answer la-block__answer--prose">
            {text || <span className="admin-muted">(left blank)</span>}
          </p>
        ))
      )}
      {answer.files.length > 0 && (
        <ul className="la-block__meta">
          {answer.files.map((file, index) => (
            <li key={index}>
              {file.filename ?? 'unnamed file'}
              {file.description ? ` — ${file.description}` : ''}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ProblemAnswerView({ answer }: { answer: ProblemAnswer }) {
  const entries = Object.entries(answer.answers);

  return (
    <div className="la-problem">
      {answer.attempts !== null && (
        <p className="la-block__meta">
          {answer.attempts} attempt{answer.attempts === 1 ? '' : 's'}
        </p>
      )}
      {entries.length === 0 ? (
        <p className="la-block__note">No response was recorded for any input on this problem.</p>
      ) : (
        <dl className="la-problem__inputs">
          {entries.map(([inputId, value]) => {
            const correctness = answer.correct_map[inputId] ?? null;
            return (
              <div key={inputId} data-correctness={correctness ?? 'unknown'}>
                <dt className="mono">{inputId}</dt>
                <dd>{formatAnswerValue(value)}</dd>
              </div>
            );
          })}
        </dl>
      )}
    </div>
  );
}

/** A problem's raw stored value, which is the input's own shape — a string, a list, a map. */
function formatAnswerValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(String).join(', ');
  return JSON.stringify(value);
}
